import { useTranslation } from 'react-i18next';
import { BookOpen, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/context/SidebarContext';
import { useAuth } from '@/hooks/useAuthHooks';
import { useAuthStore, useOrgFeatures } from '@/stores/authStore';
import { usePendingDiscoveryCount } from '@/hooks/useMonitoring';
import { useEntrepreneurAccesses } from '@/hooks/useEntrepreneurAccesses';
import {
  type EntrepreneurPlatform,
  platformRedirectPath,
} from '@/config/entrepreneurPlatforms';
import { accessibleResources, canAccessArea, UserRole } from '@/types/auth';
import {
  AREA_META,
  AREA_ORDER,
  GROUPS,
  RESOURCES,
  RESOURCE_BY_KEY,
  type Area,
} from '@/config/resources';
import { ENTREPRENEUR_NAV } from '@/config/entrepreneurNav';
import { currentPlatform, docsPath } from '@/utils/paths';
import { BaseSidebar } from './BaseSidebar';
import { SidebarMenu } from './SidebarMenu';
import { MenuConfig, MenuItem } from './types';

// Build the menu, nesting resources that share a groupId under a collapsible
// submenu (emitted at the group's first occurrence, in catalog order); other
// resources stay flat. `badges` maps a resource key → count for the review
// badge; the count rendered on the collapsed group icon is derived from its
// children at render time (see SidebarMenuItem).
const buildMenuItems = (
  keys: string[],
  badges: Record<string, number> = {}
): MenuItem[] => {
  const childrenByGroup: Record<string, MenuItem[]> = {};
  for (const key of keys) {
    const m = RESOURCE_BY_KEY[key];
    if (m.groupId) {
      (childrenByGroup[m.groupId] ??= []).push({
        id: key,
        labelKey: m.labelKey,
        icon: m.icon,
        path: m.route,
        badge: badges[key] || undefined,
      });
    }
  }
  const items: MenuItem[] = [];
  const emitted = new Set<string>();
  for (const key of keys) {
    const m = RESOURCE_BY_KEY[key];
    if (m.groupId) {
      if (!emitted.has(m.groupId)) {
        emitted.add(m.groupId);
        const g = GROUPS[m.groupId];
        items.push({
          id: `group-${m.groupId}`,
          labelKey: g.labelKey,
          icon: g.icon,
          children: childrenByGroup[m.groupId],
        });
      }
    } else {
      items.push({
        id: key,
        labelKey: m.labelKey,
        icon: m.icon,
        path: m.route,
        badge: badges[key] || undefined,
      });
    }
  }

  // Mark index/parent routes (a path that is a strict prefix of a sibling's
  // path) as `exact` so they highlight only on their own page, not on every
  // descendant route — e.g. `/org` shouldn't stay active on `/org/team`.
  const allPaths: string[] = [];
  const collect = (its: MenuItem[]) =>
    its.forEach((i) => {
      if (i.path) allPaths.push(i.path);
      if (i.children) collect(i.children);
    });
  collect(items);
  const markExact = (its: MenuItem[]) =>
    its.forEach((i) => {
      if (i.path && allPaths.some((p) => p !== i.path && p.startsWith(i.path + '/'))) {
        i.exact = true;
      }
      if (i.children) markExact(i.children);
    });
  markExact(items);

  return items;
};

// The founder portal's fixed menu. It is built from its own list rather than
// from the resource matrix because the B2C portal is gated by authentication,
// approval state and platform entitlement, never by per-role cells.
const buildPortalItems = (): MenuItem[] =>
  ENTREPRENEUR_NAV.map((entry) => ({
    id: entry.id,
    labelKey: entry.labelKey,
    icon: entry.icon,
    path: entry.route,
  }));

// A single sidebar driven by the backend role registry: it renders the
// resources the current role can view in `area`, and an app-switcher to the
// other areas the role can reach. Replaces every hardcoded sidebar.
interface DynamicSidebarProps {
  area: Area
  hideMenu?: boolean
  /**
   * Resources granted by an assignment rather than by the role registry.
   * Expert/Mentor-style surfaces use this to reuse the same catalog while
   * keeping the server-side assignment as the actual security boundary.
   */
  assignedResourceKeys?: string[]
  routeOverrides?: Record<string, string>
  /** Overrides the area's brand. The shell chrome (`/` and the catch-all) uses
   * it to show the neutral `vess.` when no platform is resolvable. */
  brandLabel?: string
}

export const DynamicSidebar = ({
  area,
  hideMenu = false,
  assignedResourceKeys = [],
  routeOverrides = {},
  brandLabel,
}: DynamicSidebarProps) => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { hasFeature } = useOrgFeatures();
  const permissions = useAuthStore((s) => s.permissions);
  const role = user?.role;

  let keys = accessibleResources(role, area, RESOURCES, permissions);

  // Assignment-scoped resources are intentionally opt-in per route. The
  // endpoint and route guard still enforce membership; this only makes the
  // accessible assigned surface visible in the shared navigation.
  keys = [...new Set([...keys, ...assignedResourceKeys])].filter(
    (key) => RESOURCE_BY_KEY[key]?.area === area
  );

  // Some backend resources are reserved for planned Gaia modules. They remain
  // in the catalog for permission parity but must not expose dead UI routes.
  keys = keys.filter((key) => !RESOURCE_BY_KEY[key].hiddenFromNavigation);

  // Una sección marcada con `platform` solo existe en ese producto: en el
  // panel de gaia no hay locations ni sources, y de las tres entradas de
  // ajustes de plataforma sobrevive la de gaia. No es un gate — el
  // StaffRoute de cada ruta sigue siendo la frontera — es no enseñar un
  // enlace a una sección que ese producto no tiene.
  const platform = currentPlatform();
  keys = keys.filter((key) => {
    const only = RESOURCE_BY_KEY[key].platform;
    return !only || only === platform;
  });

  // Review badges: pending discoveries to triage. Only fetched when the user can
  // see Discovery in this area, so other roles/portals never hit the endpoint.
  const pendingDiscoveries = usePendingDiscoveryCount(
    area === 'terminus' && keys.includes('discovery')
  );
  const badges = pendingDiscoveries ? { discovery: pendingDiscoveries } : {};

  // The portal's menu belongs to founders, not to the area: `area` is also
  // 'entrepreneur' on the 403 shell a non-founder gets from AuthWrapper, and
  // that shell must keep showing no menu at all.
  const isPortalMenu = area === 'entrepreneur' && role === UserRole.ENTREPRENEUR;
  const menuConfig: MenuConfig = {
    items: isPortalMenu ? buildPortalItems() : buildMenuItems(keys, badges),
  };

  if (Object.keys(routeOverrides).length) {
    for (const item of menuConfig.items) {
      if (item.id in routeOverrides) item.path = routeOverrides[item.id];
      item.children?.forEach((child) => {
        if (child.id in routeOverrides) child.path = routeOverrides[child.id];
      });
    }
  }

  const homeKey = keys[0];
  const homeHref = homeKey
    ? routeOverrides[homeKey] ?? RESOURCE_BY_KEY[homeKey].route
    : AREA_META[area].switcherHref;

  const switcherButton = (Icon: LucideIcon, label: string, onClick: () => void) => (
    <Button
      key={label}
      variant="ghost"
      onClick={onClick}
      className={cn(
        'text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors',
        isCollapsed ? 'h-8 w-8 p-0' : 'w-full justify-start h-8 px-3 gap-2'
      )}
    >
      <Icon className={cn('h-4 w-4', !isCollapsed && 'flex-shrink-0')} />
      {!isCollapsed && <span className="text-sm">{label}</span>}
    </Button>
  );

  const otherAreas = AREA_ORDER.filter(
    (a) =>
      a !== area &&
      canAccessArea(role, a, RESOURCES, permissions) &&
      (!AREA_META[a].requiresFeature || hasFeature(AREA_META[a].requiresFeature!))
  );
  const canAccessSuperadmin = canAccessArea(role, 'superadmin', RESOURCES, permissions);

  // An entrepreneur's platform axis (gaia / foundershub) is a completely
  // different membership mechanism than the staff area catalog above --
  // `otherAreas` is always empty for them. `area` still tells us which B2C
  // portal we're in: the gaia participant portal reuses area="gaia", the
  // foundershub-flavored one uses area="entrepreneur".
  const isEntrepreneur = role === UserRole.ENTREPRENEUR;
  const currentEntrepreneurPlatform: EntrepreneurPlatform =
    area === 'gaia' ? 'gaia' : 'foundershub';
  const { accesses: entrepreneurAccesses } = useEntrepreneurAccesses(isEntrepreneur);
  const otherPlatformAccesses = isEntrepreneur
    ? entrepreneurAccesses.filter(
        (access) => access.platform !== currentEntrepreneurPlatform && access.is_active
      )
    : [];

  const extra =
    otherAreas.length || canAccessSuperadmin || otherPlatformAccesses.length ? (
      <div className={cn('py-3', isCollapsed ? 'px-2 flex flex-col items-center gap-1' : 'px-3')}>
        {otherAreas.map((a) => {
          const meta = AREA_META[a];
          return switcherButton(meta.switcherIcon, t(meta.switcherLabelKey), () =>
            window.open(meta.switcherHref, '_self')
          );
        })}
        {otherPlatformAccesses.map((access) => {
          const meta = AREA_META[access.platform];
          return switcherButton(meta.switcherIcon, t(meta.switcherLabelKey), () =>
            window.open(platformRedirectPath(access), '_self')
          );
        })}
        {canAccessSuperadmin &&
          switcherButton(BookOpen, t('sidebar.documentationButton'), () =>
            window.open(docsPath(), '_blank')
          )}
      </div>
    ) : undefined;

  return (
    <BaseSidebar brandLabel={brandLabel ?? AREA_META[area].brandLabel} homeHref={homeHref} extra={extra}>
      {hideMenu ? (
        <div className="flex-1" />
      ) : (
        <SidebarMenu isCollapsed={isCollapsed} customConfig={menuConfig} />
      )}
    </BaseSidebar>
  );
};

export default DynamicSidebar;
